<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import { Button, ZhDecisionActions, ZhDecisionButton, ZhDetailsFold, ZhTooltip } from '@zerohuman/ui'
import { computed } from 'vue'
import type { CeoQuestion, CeoQuestionAnswer, GateRow } from '@/api'
import { gateAnswersDraftKey } from '@/lib/gateDecisionDraft'
import { askerLabel, questionCount } from '@/lib/gateLead'

/**
 * A question gate (`os.ask_ceo`), one row per question with the asking member's recommendation. Each row is settled
 * by Accept (the recommendation becomes the answer), Answer (the typed text), or Explain (the member looks into it
 * further and asks again; the text says what was unclear). Typing an answer selects Answer, unless Explain is
 * selected, where the text says what was unclear. Answer with an empty box leaves the row open. Send answers sends
 * every reply once each row has one; Reject declines the whole ask with the note as its reason. The note lives in
 * `GateDecisionActions`, which owns it for every gate; the replies are drafted here, per gate, so a reload keeps them.
 *
 * Each row shows its question and the recommendation; the question's context is the reasoning, folded under
 * "Show details" (#1760), so a row reads as what is asked and what is recommended.
 */
const props = defineProps<{ gate: GateRow; questions: CeoQuestion[]; busy: boolean }>()
const emit = defineEmits<{ submit: [answers: CeoQuestionAnswer[]]; reject: [] }>()

type Choice = 'accept' | 'answer' | 'explain'
type Draft = { text: string; choice: Choice | null }

const drafts = useStorage<Record<string, Draft>>(() => gateAnswersDraftKey(props.gate.id), {}, undefined, {
    flush: 'sync',
    writeDefaults: false,
    onError: () => {
        // Keep the rows usable when browser storage is unavailable.
    },
})

function draft(number: number): Draft {
    return drafts.value[number] ?? { text: '', choice: null }
}

function update(number: number, patch: Partial<Draft>): void {
    drafts.value = { ...drafts.value, [number]: { ...draft(number), ...patch } }
}

/** Typing an answer is choosing Answer. Under Explain the box says what was unclear, so Explain stays. */
function typeAnswer(number: number, text: string): void {
    const typed = text.trim() !== '' && draft(number).choice !== 'explain'
    update(number, typed ? { text, choice: 'answer' } : { text })
}

function settled(question: CeoQuestion): boolean {
    const row = draft(question.number)
    return row.choice === 'accept' || row.choice === 'explain' || (row.choice === 'answer' && row.text.trim() !== '')
}

const unanswered = computed(() => props.questions.filter(question => !settled(question)).length)

/**
 * Who is asking, as the enterprise names them: "Marcus (CMO)". A question comes from whichever member ran the task,
 * on any team, so the form names that member and their role and never assumes one. Just the name or just the role
 * when only one is known, and null when neither is: the wording then names nobody.
 */
const asker = computed(() => askerLabel(props.gate))

const sendHint = computed(() =>
    unanswered.value === 0
        ? `Send every answer${asker.value ? ` to ${asker.value}` : ''}. Questions marked Explain come back with more detail.`
        : `${unanswered.value} of ${questionCount(props.questions.length)} still need${unanswered.value === 1 ? 's' : ''} a reply: Accept, Answer or Explain each one first.`,
)

const status: Record<Choice, string> = {
    accept: 'Recommendation accepted',
    answer: 'Answered',
    explain: 'More detail requested',
}

const hints = computed<Record<Choice, string>>(() => ({
    accept: 'Use the recommendation as your answer',
    answer: 'Send what you typed as your answer',
    explain: asker.value
        ? `Ask ${asker.value} to look into this further and ask again with more detail`
        : 'Ask for this to be looked into further, and asked again with more detail',
}))

function send(): void {
    emit(
        'submit',
        props.questions.map(question => {
            const row = draft(question.number)
            return {
                number: question.number,
                answer: row.choice === 'accept' ? question.ctoRecommendation : row.text.trim(),
                requiresMoreDetail: row.choice === 'explain',
            }
        }),
    )
}
</script>

<template>
	<p class="text-sm font-medium">Questions for you</p>
	<p class="text-muted-foreground text-sm" data-asked-by>
		{{ asker ? `${asker} is asking, and recommends an answer to each.` : 'Each question comes with a recommended answer.' }}
		Accept it, give your own answer, or ask for more detail. Send once every question has a reply; Reject declines
		all of them.
	</p>
	<ol class="space-y-3">
		<li
			v-for="question in questions"
			:key="question.number"
			class="border-border space-y-2 rounded-md border p-3"
			:data-question="question.number"
		>
			<p class="text-sm font-medium whitespace-pre-wrap">{{ question.number }}. {{ question.question }}</p>
			<div class="border-primary bg-muted/40 rounded-sm border-l-2 px-2 py-1">
				<p class="text-muted-foreground text-xs font-medium tracking-wide uppercase" data-recommended-by>
					{{ asker ? `${asker} recommends` : 'Recommendation' }}
				</p>
				<p class="text-sm whitespace-pre-wrap" data-recommendation>{{ question.ctoRecommendation }}</p>
			</div>
			<ZhDetailsFold v-if="question.context">
				<p class="text-sm whitespace-pre-wrap">{{ question.context }}</p>
			</ZhDetailsFold>
			<textarea
				:value="draft(question.number).text"
				rows="2"
				class="border-input w-full rounded-md border bg-transparent p-2 text-sm"
				:aria-label="`Your answer to question ${question.number}`"
				:placeholder="draft(question.number).choice === 'explain' ? 'What is unclear? (optional)' : 'Your answer'"
				:disabled="busy"
				@input="typeAnswer(question.number, ($event.target as HTMLTextAreaElement).value)"
			/>
			<div class="flex flex-wrap items-center gap-2">
				<ZhTooltip v-for="choice in (['accept', 'answer', 'explain'] as const)" :key="choice" :text="hints[choice]">
					<Button
						type="button"
						size="sm"
						:variant="draft(question.number).choice === choice ? 'default' : 'outline'"
						:aria-pressed="draft(question.number).choice === choice"
						:disabled="busy"
						@click="update(question.number, { choice })"
					>
						{{ choice === 'accept' ? 'Accept' : choice === 'answer' ? 'Answer' : 'Explain' }}
					</Button>
				</ZhTooltip>
				<span v-if="settled(question)" class="text-muted-foreground text-xs" data-status>
					{{ status[draft(question.number).choice as Choice] }}
				</span>
			</div>
		</li>
	</ol>
	<slot name="note" />
	<!-- The row every decision card shares: its commands, then the card's link on the far edge. -->
	<ZhDecisionActions>
		<template #commands>
			<!-- A disabled button swallows hover, so the span carries the tooltip and says what is still missing. -->
			<ZhTooltip :text="sendHint">
				<span tabindex="0" class="inline-flex" data-submit-hint>
					<ZhDecisionButton :disabled="busy || unanswered > 0" @click="send">Send answers</ZhDecisionButton>
				</span>
			</ZhTooltip>
			<ZhTooltip text="Decline all of these questions. The note is given as the reason.">
				<ZhDecisionButton tone="reject" :disabled="busy" @click="emit('reject')">Reject</ZhDecisionButton>
			</ZhTooltip>
		</template>
		<template v-if="$slots.link" #link><slot name="link" /></template>
	</ZhDecisionActions>
</template>
